import { create } from 'zustand'
import { api } from '@/lib/api'

interface WaitlistEntry {
  id: string
  name: string
  email: string
  status: 'unconfirmed' | 'pending' | 'approved' | 'rejected'
  source: string | null
  ipAddress: string | null
  confirmedAt: string | null
  createdAt: string
  updatedAt: string
}

interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

interface WaitlistStats {
  unconfirmed: number
  pending: number
  approved: number
  rejected: number
  total: number
}

interface WaitlistState {
  entries: WaitlistEntry[]
  pagination: Pagination
  stats: WaitlistStats | null
  statusFilter: string
  searchQuery: string
  selectedIds: Set<string>
  isLoading: boolean
  error: string | null

  setStatusFilter: (status: string) => void
  setSearchQuery: (query: string) => void
  toggleSelection: (id: string) => void
  selectAll: () => void
  clearSelection: () => void
  fetchEntries: (page?: number) => Promise<void>
  fetchStats: () => Promise<void>
  updateStatus: (id: string, status: 'approved' | 'rejected') => Promise<void>
  batchUpdate: (status: 'approved' | 'rejected') => Promise<void>
  deleteEntry: (id: string) => Promise<void>
}

export const useWaitlistStore = create<WaitlistState>((set, get) => ({
  entries: [],
  pagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
  stats: null,
  statusFilter: '',
  searchQuery: '',
  selectedIds: new Set(),
  isLoading: false,
  error: null,

  setStatusFilter: (statusFilter) => {
    set({ statusFilter, selectedIds: new Set() })
    get().fetchEntries(1)
  },

  setSearchQuery: (searchQuery) => {
    set({ searchQuery, selectedIds: new Set() })
    get().fetchEntries(1)
  },

  toggleSelection: (id) => {
    const selected = new Set(get().selectedIds)
    if (selected.has(id)) selected.delete(id)
    else selected.add(id)
    set({ selectedIds: selected })
  },

  selectAll: () => {
    set({ selectedIds: new Set(get().entries.map((e) => e.id)) })
  },

  clearSelection: () => set({ selectedIds: new Set() }),

  fetchEntries: async (page = 1) => {
    set({ isLoading: true, error: null })
    const { statusFilter, searchQuery } = get()
    try {
      const res = await api.get<{
        data: { entries: WaitlistEntry[]; pagination: Pagination }
      }>('/api/v1/admin/waitlist', {
        page,
        limit: 25,
        status: statusFilter || undefined,
        search: searchQuery || undefined,
      })
      set({
        entries: res.data.entries,
        pagination: res.data.pagination,
        isLoading: false,
      })
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false })
    }
  },

  fetchStats: async () => {
    try {
      const res = await api.get<{ data: WaitlistStats }>('/api/v1/admin/waitlist/stats')
      set({ stats: res.data })
    } catch (err) {
      console.error('Failed to fetch waitlist stats:', err)
    }
  },

  updateStatus: async (id, status) => {
    await api.patch(`/api/v1/admin/waitlist/${id}`, { status })
    get().fetchEntries(get().pagination.page)
    get().fetchStats()
  },

  batchUpdate: async (status) => {
    const ids = Array.from(get().selectedIds)
    if (ids.length === 0) return
    await api.post('/api/v1/admin/waitlist/batch', { ids, status })
    set({ selectedIds: new Set() })
    get().fetchEntries(get().pagination.page)
    get().fetchStats()
  },

  deleteEntry: async (id) => {
    await api.delete(`/api/v1/admin/waitlist/${id}`)
    get().fetchEntries(get().pagination.page)
    get().fetchStats()
  },
}))
